import { ChevronUp } from '@raydiant/raydial/components/Icons';
import { useSelector } from 'react-redux';
import {
  selectSearchQuery,
  selectSortOptions,
} from '../../PlaylistSelector/selectors';
import LibraryTreeV2Item from './LibraryTreeV2Item';
import { DEFAULT_QUERY } from '../../PlaylistSelector/PlaylistSelectorV2';
import { useLibrary } from '../../../hooks';
import ApplicationIcon from '../../../pages/LibraryPage/v2/components/ApplicationIcon';
import {
  getPresentationData,
  sortContent,
  mergeLibrary,
} from '../../../pages/LibraryPage/utils';
import Spinner from '@raydiant/raydial/components/Spinner';
import Error from '../../Error/Error';
import { memo, useMemo } from 'react';
import { Content } from '@raydiant/api-client-js';

interface PlaylistSelectorProps {
  openedFolders: {
    [key: string]: boolean;
  };
  selectedPlaylistId: string;
  onFolderClick: (id: string) => void;
  onSelectPlaylist?: (playlistId: string) => void;
  onAssignPlaylist?: (content: Content) => void;
  scope: string;
  profileId: string;
  folderId: string;
  isRootFolder?: boolean;
  level?: number;
}

const LibraryTreeV2: React.FC<PlaylistSelectorProps> = ({
  openedFolders,
  onFolderClick,
  selectedPlaylistId,
  onSelectPlaylist,
  onAssignPlaylist,
  scope,
  profileId,
  folderId,
  isRootFolder = false,
  level = 0,
}) => {
  const searchQuery = useSelector(selectSearchQuery);
  const sortOptions = useSelector(selectSortOptions);

  const { data: libraryData, isLoading: libraryLoading } = useLibrary(
    {
      ...DEFAULT_QUERY,
      scope,
      profile_id: searchQuery ? '' : profileId,
      folder_id: searchQuery ? '' : folderId || '',
      search: searchQuery,
    },
    { enabled: isRootFolder || openedFolders[folderId] },
  );

  if (isRootFolder && libraryLoading) {
    <div className="flex justify-center items-center flex-1">
      <Spinner size="large" />
    </div>;
  }

  const mergedLibrary = useMemo(
    () => mergeLibrary(libraryData?.data || []),
    [libraryData],
  );
  const folders = useMemo(
    () => mergedLibrary.filter((content) => content.resourceType === 'folder'),
    [mergedLibrary],
  );
  const playlists = useMemo(
    () =>
      mergedLibrary.filter((content) => content.resourceType === 'playlist'),
    [mergedLibrary],
  );
  const sortedFolders = useMemo(
    () => sortContent(folders, sortOptions),
    [folders, sortOptions],
  );
  const sortedPlaylists = useMemo(
    () => sortContent(playlists, sortOptions),
    [playlists, sortOptions],
  );
  const sortedLibraryData = useMemo(() => {
    return [
      ...(searchQuery.length > 0 ? [] : sortedFolders),
      ...sortedPlaylists,
    ];
  }, [searchQuery, sortedFolders, sortedPlaylists]);
  const isEmpty = sortedLibraryData.length === 0;

  const renderEmptyState = () => {
    const currentUserLibrary = !profileId && scope === 'owned' && !searchQuery;
    const sharedLibrary = !profileId && scope === 'shared' && !searchQuery;

    if (searchQuery) {
      return (
        <div className="px-2 py-4">
          <Error
            title="No results"
            message="There’s no data for the selected filters, please try different search parameter"
          />
        </div>
      );
    }

    if (currentUserLibrary) {
      return (
        <div className="px-2 py-4">
          <Error
            title="No results"
            message="Hm, there’s nothing to add. Please create playlist and add some of your presentations!"
          />
        </div>
      );
    }

    if (sharedLibrary) {
      return (
        <div className="px-2 py-4">
          <Error
            title="No results"
            message="Hm, there’s nothing to add. Please ask others to share content"
          />
        </div>
      );
    }

    return <Error />;
  };

  if (isRootFolder && isEmpty) {
    return renderEmptyState();
  }

  return (
    <div>
      {sortedLibraryData.map((content) => (
        <div key={content.id} className="flex flex-col">
          {content.resourceType === 'folder' && (
            <>
              <div
                className="flex cursor-pointer items-center h-12 px-4 gap-2 rounded-2xl hover:bg-blue-50"
                onClick={() => onFolderClick(content.id)}
              >
                <ApplicationIcon
                  className=""
                  url={getPresentationData(content).applicationIconUrl}
                />
                <div className="flex-1 truncate text-lg font-regular text-black p-2.5">
                  {content.name}
                </div>
                {openedFolders[content.id] ? (
                  <ChevronUp style={{ transform: 'scale(1, -1)' }} />
                ) : (
                  <ChevronUp />
                )}
              </div>

              {openedFolders[content.id] && (
                <div className="pl-2">
                  <LibraryTreeV2
                    openedFolders={openedFolders}
                    selectedPlaylistId={selectedPlaylistId}
                    onSelectPlaylist={onSelectPlaylist}
                    onAssignPlaylist={onAssignPlaylist}
                    onFolderClick={onFolderClick}
                    scope={scope}
                    profileId={profileId}
                    folderId={content.id}
                    isRootFolder={false}
                    level={level + 1}
                  />
                </div>
              )}
            </>
          )}

          {content.resourceType === 'playlist' && (
            <LibraryTreeV2Item
              onClick={
                onSelectPlaylist ? () => onSelectPlaylist(content.id) : () => {}
              }
              iconUrl={getPresentationData(content).applicationIconUrl}
              label={content.name}
              showButton={selectedPlaylistId === content.id}
              onButtonClick={
                onAssignPlaylist ? () => onAssignPlaylist(content) : () => {}
              }
              buttonLabel="Assign"
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default memo(LibraryTreeV2);
